import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { PetSpeciesFilterableType } from 'src/app/interfaces/pet';
import { MembershipSelectStepStates, ProductType, User } from 'src/app/interfaces/user';
import { PlanFeatureService } from 'src/app/services/common/plan-feature.service';
import { MemberShipOptionsService } from 'src/app/services/membership-options/membership-options.service';
import { UserService } from 'src/app/services/user/user.service';
import { PricingInfoLine } from 'src/app/shared/components/pricing-table/price-card/price-card.component';

export enum PricingTableSubTypes {
  IDENTIBASEPLUS = 'IDENTIBASEPLUS',
  IDENTIALERT = 'IDENTIALERT',
  IDENTIFIND = 'IDENTIFIND',
  PitPat = 'PitPat',
  SmartTag = 'SmartTag',
}

@Component({
  selector: 'app-pricing-table',
  templateUrl: './pricing-table.component.html',
  styleUrls: ['./pricing-table.component.scss'],
})
export class PricingTableComponent implements OnInit, OnChanges {
  @Input() disabled: boolean;
  @Input() pricingPlans: any;
  @Input() viewOnly: boolean;
  @Input() public user: User;
  @Input() buttonLabel: string;
  @Input() hidePrices: boolean;
  @Input() showShortDescription: boolean;
  @Input() showInfoLine: boolean;
  @Input() showFeatures: boolean;
  @Input() showPromo: boolean;
  @Input() petName: string;
  @Input() showWideIdentiAlertCard: boolean;
  @Input() hideIcons: boolean;
  @Input() hasBuyNow: boolean;
  @Input() appPriceCardFeatureText: string[];
  @Input() appAlertPriceCardFeatureText: string[];
  @Input() showPeachBanner: boolean;

  @Output() onClosePromo = new EventEmitter<string>();

  @Output() onSelect: EventEmitter<string> = new EventEmitter();
  @Output() onNavigateToProductPage: EventEmitter<string> = new EventEmitter();

  public doesTheUserHaveCat: boolean = false;
  public doesTheUserHaveDog: boolean = false;
  public identiFindSelected = 'identiFindSelected';
  public productType = ProductType;
  public planIdIdentiAlert: string;
  public PricingTableSubTypes = PricingTableSubTypes;
  public promoPopupOpen: boolean = false;
  public pitPatAvailable: boolean = true;
  public doesTheUserHaveOnlyCat: boolean = false;
  public pitPatPricingInfoLines: PricingInfoLine[] = [
    {
      priceAmount: '£129',
      priceDescription: 'for identibase+, identialert and identifind plan holders',
    },
    { priceAmount: '£149', priceDescription: 'for Basic plan holders' },
  ];
  public pitPatProductId: string;
  public smartTagAvailable: boolean = true;
  public smartTagProductId: string;
  public smartTagPricingInfoLines: PricingInfoLine[] = [
    {
      priceAmount: '£10',
      priceDescription: 'for identibase+, identialert and identifind plan holders',
    },
  ];

  public identibasePlusAvailable: boolean = true;
  public identiAlertAvailable: boolean = true;
  public identiFindAvailable: boolean = true;
  public lineFindAvailable: boolean = true;
  public membershipSelectStepNotCompleted: boolean = true;
  public showTitles: boolean = false;
  public disabledSmartTag: boolean = true;
  public mainPlanContent = [];
  closePromoPopup() {
    this.promoPopupOpen = false;
    this.onClosePromo.emit();
  }
  public isBrowser: boolean;

  constructor(
    public userService: UserService,
    public membershipOptionsService: MemberShipOptionsService,
    public planFeatureService: PlanFeatureService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showPromo) {
      this.showPromo = changes.showPromo.currentValue;
      this.promoPopupOpen = this.showPromo;
    }
  }

  getFeaturesFor(plan: string): any[] {
    return this.planFeatureService.getFeaturesFor(plan);
  }

  getMainPlanCardContent(petName?: string) {
    return this.planFeatureService.getMainPlanContent(petName);
  }

  select(planId?: string): void {
    if (planId == this.identiFindSelected) {
      if (this.doesTheUserHaveCat && !this.doesTheUserHaveDog) {
        this.onSelect.emit(this.pricingPlans['identiAlertFindCat']);
      } else {
        this.onSelect.emit(this.pricingPlans['identiAlertFind']);
      }
    } else {
      this.onSelect.emit(planId);
    }
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {
    // Setting the default option for the identiAlert plan
    if (!this.viewOnly) {
      this.planIdIdentiAlert = this.pricingPlans.identiAlertAnnual;
    }

    this.mainPlanContent = this.getMainPlanCardContent(this.petName);
    this.pitPatProductId = this.pricingPlans.pitPatGpsTracker;

    if (this.user) {
      this.doesTheUserHaveCat = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Cat
      );
      this.doesTheUserHaveDog = !!this.user.pets.find(
        (pet) => pet.speciesFilter === PetSpeciesFilterableType.Dog
      );

      if (this.doesTheUserHaveCat && !this.doesTheUserHaveDog) {
        this.doesTheUserHaveOnlyCat = true;
      }

      if (
        this.userService.isUserAlertOrFind(this.user.productType) ||
        this.user.productType === 5 // we include identibase+ users as well
      ) {
        this.smartTagProductId = this.pricingPlans.smartTagForAlertAndFind;
      } else {
        this.smartTagProductId = this.pricingPlans.smartTagForBase;
      }

      this.identibasePlusAvailable = this.membershipOptionsService.identiBasePlusAvailable(
        this.user
      );
      this.identiAlertAvailable = this.membershipOptionsService.identiAlertAvailable(this.user);
      this.identiFindAvailable = this.membershipOptionsService.identiFindAvailable(this.user);

      if (this.userService.isUserBasic(this.user.productType)) {
        this.pitPatProductId = this.pricingPlans.pitPatGpsTrackerBasic;
      }

      this.lineFindAvailable =
        (this.identibasePlusAvailable || this.identiAlertAvailable || this.identiFindAvailable) &&
        (this.pitPatAvailable || this.smartTagAvailable);
      this.membershipSelectStepNotCompleted =
        this.user.membershipSelectStepCompleted === MembershipSelectStepStates.NotUpdated;
      this.showTitles = this.lineFindAvailable;
      
    }

    this.mainPlanContent = this.getMainPlanCardContent(this.petName);
  }

  public doesTheUserHaveOnlyCats() {
    return this.doesTheUserHaveCat && !this.doesTheUserHaveDog;
  }
}
